import React from 'react';
import { format, isToday, isSameMonth, startOfMonth, endOfMonth, eachDayOfInterval, isWeekend, subDays } from 'date-fns';
import { hr } from 'date-fns/locale';
import { Home, Building2, Plane, Thermometer, Calendar, Plus, Star, Users, Palmtree, Baby, Paperclip } from 'lucide-react';
import { formatCroatianMonth } from '../../i18n/languages';
import { useYearStatus } from '../../contexts/YearStatusContext';
// Holiday functions now come from useCalendarData hook via props

const statusIcons = {
  office: Building2,
  home: Home,
  vacation: Palmtree,
  sick: Thermometer,
  other: Calendar,
  business_trip: Plane,
  parental_leave: Baby
};

const statusColors = {
  office: '#3b82f6',
  home: '#10b981',
  vacation: '#079C0C',
  sick: '#ef4444',
  other: '#8b5cf6',
  business_trip: '#27BBF5',
  parental_leave: '#f59e0b'
};

function CalendarTable({
  month,
  teamMembers,
  getStatusForUserAndDate,
  currentUser,
  handleCellClick,
  handleMouseDown,
  handleMouseEnter,
  handleMouseUp,
  isDateSelected,
  currentLanguage,
  t,
  isCroatianHoliday = () => false,
  getHolidayName = () => null,
  collectiveVacations = [],
  currentUserRole = 'employee',
  adminEditMode = false,
  userEmploymentData = {},
  getFullNameFromDisplay = (name) => name
}) {
  const { yearStatus } = useYearStatus();
  const currentOpenYear = yearStatus?.currentOpenYear;

  const monthStart = startOfMonth(month);
  const monthEnd = endOfMonth(month);
  const days = eachDayOfInterval({ start: monthStart, end: monthEnd });

  // Helper function to determine if a cell should be editable
  const isCellEditable = (day, member) => {
    const today = new Date();
    const currentMonth = today.getMonth();
    const dayMonth = day.getMonth();
    const dayYear = day.getFullYear();

    // Basic restrictions that apply to everyone
    if (isWeekend(day) || isCroatianHoliday(day) || isCollectiveVacation(day)) {
      return false;
    }

    // Check employment start date restriction for the target user
    const employmentData = userEmploymentData[member];
    if (employmentData && employmentData.employment_start_date) {
      const employmentStartDate = new Date(employmentData.employment_start_date);
      // Normalize dates to midnight of the same day
      const dayStart = new Date(day);
      dayStart.setHours(0, 0, 0, 0);

      const employmentStart = new Date(employmentStartDate);
      employmentStart.setHours(0, 0, 0, 0);

      if (dayStart < employmentStart) {
        return false; // Cannot edit dates before employment start
      }
    }

    // Admin with edit mode enabled can edit anyone's status for any month
    if (currentUserRole === 'admin' && adminEditMode) {
      return true;
    }

    // Regular users and admins without edit mode can only edit their own statuses
    if (member !== currentUser) {
      return false;
    }

    const currentSystemYear = today.getFullYear();

    // Allow clicks on closed years (error will be handled in Dashboard)
    // For the current open year: allow all months (no month restrictions)
    // For system year: only allow current month and future months
    if (dayYear < currentSystemYear ||
      (dayYear === currentSystemYear && dayMonth < currentMonth)) {
      return false;
    }

    return true;
  };

  // Function to determine if a vacation day is from carryover or current year
  const getVacationSource = (userDisplayName, date) => {
    const status = getStatusForUserAndDate(userDisplayName, date);

    // Use backend-provided vacation_source if available
    if (status && status.vacation_source) {
      return status.vacation_source;
    }

    // Fallback to current_year if no vacation_source provided
    return 'current_year';
  };

  // Check if a date is a collective vacation
  const isCollectiveVacation = (date) => {
    const dateString = format(date, 'yyyy-MM-dd');
    if (collectiveVacations.length > 0) {
      const isCollective = collectiveVacations.some(vacation => {
        // Handle both ISO timestamp and simple date formats
        const vacationDate = vacation.date.includes('T') ?
          vacation.date.split('T')[0] : vacation.date;
        return vacationDate === dateString;
      });
      return isCollective;
    }
    return false;
  };

  // Get collective vacation info for a date
  const getCollectiveVacationInfo = (date) => {
    const dateString = format(date, 'yyyy-MM-dd');
    return collectiveVacations.find(vacation => {
      // Handle both ISO timestamp and simple date formats
      const vacationDate = vacation.date.includes('T') ?
        vacation.date.split('T')[0] : vacation.date;
      return vacationDate === dateString;
    });
  };

  const renderStatusIcon = (status, member = null, size = 16, isCurrentUserRow = false, date = null) => {
    if (!status) {
      // Only show plus icon for current user's weekdays (not weekends or holidays)
      return isCurrentUserRow && date && !isWeekend(date) && !isCroatianHoliday(date) ? <Plus size={size} color="#d1d5db" /> : null;
    }

    const IconComponent = statusIcons[status.status];
    const color = statusColors[status.status];

    if (!IconComponent) {
      return <Plus size={size} color="#d1d5db" />;
    }

    let showAttachmentBadge = Boolean(status.attachment_url);
    if (showAttachmentBadge && date && member) {
      const previousDay = subDays(date, 1);
      const previousStatus = getStatusForUserAndDate(member, previousDay);
      if (previousStatus && previousStatus.attachment_url === status.attachment_url) {
        showAttachmentBadge = false;
      }
    }

    return (
      <div style={{ position: 'relative', display: 'inline-flex' }}>
        <IconComponent size={size} color={color} />
        {showAttachmentBadge && (
          <span
            style={{
              position: 'absolute',
              bottom: -16,
              right: -10,
              backgroundColor: '#ffffff',
              borderRadius: '50%',
              padding: '2px',
              boxShadow: '0 1px 2px rgba(15, 23, 42, 0.2)'
            }}
          >
            <Paperclip size={15} color={color} />
          </span>
        )}
      </div>
    );
  };

  return (
    <div style={{ marginBottom: '32px' }}>
      <h3 style={{
        fontSize: '18px',
        fontWeight: '600',
        color: '#1f2937',
        marginBottom: '16px',
        textAlign: 'center'
      }}>
        {formatCroatianMonth(month, currentLanguage) ||
          format(month, t('monthYear'), { locale: currentLanguage === 'hr' ? hr : undefined })}
      </h3>

      <div style={{
        overflowX: 'auto',
        backgroundColor: 'white',
        borderRadius: '12px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        border: '1px solid #e5e7eb',
        width: '100%',
        maxHeight: '80vh', // Limit height to enable scrolling
        overflowY: 'auto', // Enable vertical scrolling
        position: 'relative' // Required for sticky positioning context
      }}>
        <table style={{
          width: '100%',
          borderCollapse: 'collapse',
          minWidth: '1200px',
          tableLayout: 'fixed'
        }}>
          <thead style={{ position: 'sticky', top: 0, zIndex: 10 }}>
            <tr style={{ backgroundColor: '#f9fafb' }}>
              <th style={{
                padding: '16px',
                textAlign: 'left',
                fontWeight: '600',
                color: '#374151',
                border: '1px solid #e5e7eb',
                backgroundColor: '#f9fafb',
                width: '150px',
                minWidth: '150px',
                position: 'sticky',
                left: 0,
                zIndex: 20 // Higher than the header to ensure it stays on top at the corner
              }}>
                {t('employee')}
              </th>
              {days.map(day => {
                const isHoliday = isCroatianHoliday(day);
                const holidayInfo = isHoliday ? { name: getHolidayName(day, currentLanguage) } : null;

                return (
                  <th key={day.toISOString()} style={{
                    padding: '12px 8px',
                    textAlign: 'center',
                    fontWeight: '500',
                    color: isToday(day) ? '#059669' : (isHoliday ? '#dc2626' : '#6b7280'),
                    border: '1px solid #e5e7eb',
                    backgroundColor: isToday(day) ? '#f0fdf4' : (isHoliday ? '#fef2f2' : (isWeekend(day) ? '#d4d4d4' : '#f9fafb')),
                    fontSize: '12px',
                    width: `${100 / days.length}%`,
                    position: 'relative'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
                      {format(day, 'EEE', { locale: currentLanguage === 'hr' ? hr : undefined })}
                      {isHoliday && <Star size={10} color="#dc2626" />}
                    </div>
                    <div style={{
                      fontSize: '14px',
                      fontWeight: isToday(day) ? '700' : '600',
                      color: isToday(day) ? '#059669' : (isHoliday ? '#dc2626' : '#1f2937')
                    }}>
                      {format(day, 'd')}
                    </div>
                    {isHoliday && holidayInfo && (
                      <div style={{
                        fontSize: '9px',
                        color: '#dc2626',
                        fontWeight: '500',
                        marginTop: '2px',
                        lineHeight: '1',
                        maxWidth: '100%',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }} title={holidayInfo.name}>
                        {holidayInfo.name.length > 8 ? holidayInfo.name.substring(0, 8) + '...' : holidayInfo.name}
                      </div>
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {teamMembers.map((member, memberIndex) => {
              const isCurrentUserRow = member === currentUser;
              return (
                <tr key={member} style={{
                  backgroundColor: memberIndex % 2 === 0 ? '#ffffff' : '#f9fafb',
                  borderBottom: isCurrentUserRow ? '3px solid #3b82f6' : 'none'
                }}>
                  <td title={getFullNameFromDisplay(member)} style={{
                    padding: '16px',
                    fontWeight: '500',
                    color: '#1f2937',
                    border: '1px solid #e5e7eb',
                    backgroundColor: '#f9fafb',
                    width: '150px',
                    minWidth: '150px',
                    position: 'sticky',
                    left: 0,
                    zIndex: 5 // Lower than the header but still sticky
                  }}>
                    {member === currentUser ? `${member} (${t('you')})` : member}
                  </td>
                  {days.map(day => {
                    const status = getStatusForUserAndDate(member, day);
                    const isCurrentUserRow = member === currentUser;
                    const isCurrentMonth = isSameMonth(day, month);
                    const isHoliday = isCroatianHoliday(day);
                    const holidayInfo = isHoliday ? { name: getHolidayName(day, currentLanguage) } : null;
                    const isCollectiveVacationDay = isCollectiveVacation(day);
                    const collectiveVacationInfo = isCollectiveVacationDay ? getCollectiveVacationInfo(day) : null;

                    // Check if this is a vacation day and determine source
                    const isVacationDay = status && status.status === 'vacation';
                    const vacationSource = isVacationDay ? getVacationSource(member, day) : null;
                    // A vacation day dated beyond the currently-open year is a day
                    // pre-booked ahead of that year actually opening. Show it as that
                    // year's own vacation rather than carryover/current-year - the
                    // carryover-vs-current-year split only becomes meaningful once the
                    // year closes and that day's real source is finalized (at which
                    // point this falls through to the normal coloring below).
                    const isFutureYearBooking = isVacationDay && currentOpenYear != null && day.getFullYear() > currentOpenYear;

                    // Check if this cell is selected (pass the user to properly highlight in admin mode)
                    const isCellSelected = isDateSelected(day, member);
                    return (
                      <td
                        key={`${member}-${day.toISOString()}`}
                        style={{
                          padding: '8px',
                          textAlign: 'center',
                          border: '1px solid #e5e7eb',
                          backgroundColor: isCellSelected ? '#dbeafe' :
                            isToday(day) ? '#f0fdf4' :
                              isCollectiveVacationDay ? '#fef3c7' :
                                isFutureYearBooking ? '#BBE1FC' : // Pre-close booking into a not-yet-open year
                                  isVacationDay && vacationSource === 'carryover' ? '#f3f4f6' : // Light grey for carryover
                                    isVacationDay && vacationSource === 'current_year' ? '#BBE1FC' : // Light blue for current year
                                      isHoliday ? '#fef2f2' :
                                        isWeekend(day) ? '#d4d4d4' : 'white',
                          cursor: isCellEditable(day, member) ? 'pointer' : 'default',
                          opacity: isCurrentMonth ? 1 : 0.3,
                          outline: isCellSelected ? '2px solid #3b82f6' : 'none',
                          outlineOffset: '-2px',
                          width: `${100 / days.length}%`,
                          position: 'relative'
                        }}
                        onClick={(e) => isCellEditable(day, member) && handleCellClick(day, member, member, e)}
                        onMouseDown={(e) => isCellEditable(day, member) && handleMouseDown(day, member, member, e)}
                        onMouseEnter={(e) => isCellEditable(day, member) && handleMouseEnter(day, member, member, e)}
                        onMouseUp={handleMouseUp}
                        title={isCollectiveVacationDay && collectiveVacationInfo ?
                          `${currentLanguage === 'hr' ? 'Kolektivni godišnji' : 'Collective vacation'}${collectiveVacationInfo.description ? ': ' + collectiveVacationInfo.description : ''}` :
                          (isHoliday && holidayInfo ? holidayInfo.name :
                            (status ?
                              (isFutureYearBooking ?
                                `${t(`statusTypes.${status.status}`)} (${day.getFullYear()})` :
                                status.status === 'vacation' && vacationSource ?
                                  `${t(`statusTypes.${status.status}`)} (${vacationSource === 'carryover' ? (currentLanguage === 'hr' ? 'Stari' : 'Carryover') : (currentLanguage === 'hr' ? 'Tekuća godina' : 'Current year')})` :
                                  t(`statusTypes.${status.status}`)) :
                              undefined))}
                      >
                        <div style={{ position: 'relative' }}>
                          {isCollectiveVacationDay ? (
                            <Users size={20} color="#f59e0b" />
                          ) : (
                            renderStatusIcon(status, member, 20, isCurrentUserRow, day)
                          )}
                          {isHoliday && (
                            <div style={{
                              position: 'absolute',
                              top: '-2px',
                              right: '-2px',
                              zIndex: 1
                            }}>
                              <Star size={12} color="#dc2626" />
                            </div>
                          )}
                        </div>
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default CalendarTable;
